import React, { useMemo, useRef } from 'react'
import { Redirect, useParams } from 'react-router-dom'

import { IS_EMBEDDED } from '@sketch/constants'

import { useForTablet, ErrorMessage, LoadingState } from '@sketch/components'
import { SharePageCanvas } from './components/SharePageCanvas'
import {
  AnnotationQueryVariablesProvider,
  RouteParams,
  RouteProps,
  getVersionRenderStatus,
  useQueryParams,
} from '@sketch/modules-common'
import PageAnnotationsOverlayContext from './components/PageAnnotationsOverlayContext'
import PageCanvasViewHeader from './components/PageCanvasViewHeader'
import { LoadingMessage } from '../../shares/components/LoadingMessage'

import {
  isUpgradeToLatestNeeded,
  UpgradeToLatestVersion,
  useVersioning,
} from '../../versioning'

import { PageCanvasSupportGate } from './components/PageCanvasSupportGate'
import {
  DocumentMobileToolbar,
  isMobileToolboxItem,
} from '../components/DocumentMobileToolbar'
import {
  CanvasRendererProvider,
  useStatus,
} from '@sketch-hq/sketch-web-renderer'

import { usePlaceDraftAnnotation } from '../../annotations/hooks'
import ShareReadyGuard from '../components/ShareReadyGuard'
import { useGetVersionQueryWithPolling } from '../operations'
import { ReactComponent as DocumentIcon } from '@sketch/icons/document-64'
import { DraftAnnotationNotifier } from '../../annotations/components'

import { ErrorWrapper, StyledCanvasViewWrapper } from './PageCanvasView.styles'
import { DocumentSidebarLayoutExtraProps } from '../components/DocumentSidebarLayout'
import { useCanvasZoom } from '../../../components/WebRendererCanvas/hooks'

import { Panel } from '../components/Panel'
import { FrameInspectorProvider } from '../FrameView/components/FrameInspectorProvider'
import { FloatingZoom } from '../FrameView/components/FloatingZoom'
import {
  CanvasLeftPanel,
  CanvasLeftPanelResizingProvider,
} from '../../canvas/components/CanvasLeftPanel'
import { CanvasInteractionsProvider } from '../../canvas/components/CanvasInteractions'
import { SelectedFrameProvider } from '../../canvas/components/SelectedFrameContext'
import { INSPECTOR_SEGMENT } from '../constants'
import { GridsAndLayoutsProvider } from '../components/GridsAndLayoutsProvider'
import { PageSelector } from './components/PageSelector'
import { useGetWorkspaceQuery } from '@sketch/gql-types'
import { useGetActiveSidebarTab } from '../components/ShareSidebarTabContext'

const PageCanvasMobileToolBox = ({ hideSearch }: { hideSearch?: boolean }) => {
  const canvasStatus = useStatus()
  const [zoom, setPageZoom] = useCanvasZoom()
  const [placeDraftAnnotation, setPlaceDraftAnnotation] =
    usePlaceDraftAnnotation() || []

  const MOBILE_TOOLBAR_ITEMS = [
    'doc-info',
    'search',
    'zoom',
    'add-comment',
    'versions',
    'comments',
  ].filter(isMobileToolboxItem)

  return (
    <DocumentMobileToolbar
      items={MOBILE_TOOLBAR_ITEMS}
      zoom={zoom}
      setZoom={setPageZoom}
      isDraftAnnotationEnabled={placeDraftAnnotation}
      setPlaceDraftAnnotation={setPlaceDraftAnnotation}
      disableAddAnnotationButton={canvasStatus?.type !== 'READY'}
      hideSearch={hideSearch}
    />
  )
}

type PageCanvasViewProps = RouteProps<'SHARE_VIEW' | 'SHARE_PAGE_CANVAS_VIEW'> &
  DocumentSidebarLayoutExtraProps & {
    performPresentationFileCheck?: boolean
  }

export default function PageCanvasView(props: PageCanvasViewProps) {
  const {
    HeaderPortal,
    SidebarRightPortal,
    setSidebarLeftOpen,
    toggleSidebarRight,
    isSidebarRightOpen,
    performPresentationFileCheck = false,
  } = props

  const activeSidebarTab = useGetActiveSidebarTab()
  const isInspectPanelOpen = activeSidebarTab === INSPECTOR_SEGMENT

  const canvasRef = useRef<HTMLDivElement>(null)

  const isTabletAndBigger = useForTablet()
  const showPagesList = !IS_EMBEDDED

  const queryParams = useQueryParams<'SHARE_PAGE_CANVAS_VIEW' | 'SHARE_VIEW'>()
  const params = useParams<
    RouteParams<'SHARE_PAGE_CANVAS_VIEW'> | RouteParams<'SHARE_VIEW'>
  >()

  const versioning = useVersioning()
  const {
    share,
    versionShortId,
    versionIdentifier,
    isViewingLatestVersion,
    hasPendingPatches,
  } = versioning

  const { data: workspaceData } = useGetWorkspaceQuery({
    variables: { identifier: share.workspace.identifier },
    fetchPolicy: 'cache-first',
    // Skip for non-members to avoid unauthorized GraphQL errors
    skip: !share.workspace.userIsMember,
  })

  const isWorkspaceEncrypted =
    workspaceData?.workspace.features.encryptionEnabled ?? false

  const currentVersion = !versioning.loading ? versioning.currentVersion : null

  const { data, error } = useGetVersionQueryWithPolling({
    variables: {
      shareIdentifier: share.identifier,
      versionShortId,
    },
  })

  const permanentPageIdURL = 'pageUUID' in params ? params.pageUUID : undefined
  const firstDocumentPageIdentifier =
    currentVersion?.document?.pages?.entries[0]?.uuid

  const permanentPageId =
    permanentPageIdURL || firstDocumentPageIdentifier || ''

  const subjects = useMemo(
    () => [{ type: 'PAGE' as const, permanentId: permanentPageId }],
    [permanentPageId]
  )

  /**
   * We should redirect to the SHARE_VIEW when:
   * - The pageUUID matches the permanent identifier of the first page (Consistency)
   * - There's a search query arg and we need to show the results grid
   */
  const shouldRedirectToShareView =
    (firstDocumentPageIdentifier &&
      permanentPageIdURL &&
      permanentPageIdURL === firstDocumentPageIdentifier) ||
    'search' in queryParams

  if (isUpgradeToLatestNeeded(error)) {
    return <UpgradeToLatestVersion error={error} />
  }

  if (shouldRedirectToShareView) {
    const pathname = versioning.getPathname({
      routeKey: 'SHARE_VIEW',
      routeParams: {
        shareID: versioning.share.identifier,
      },
    })

    return <Redirect to={{ ...props.location, pathname }} />
  }

  const disableAnnotations = !share.commentsEnabled || IS_EMBEDDED
  const parsedRenderStatus = getVersionRenderStatus(data?.share?.version)

  const isCurrentVersionStarred = currentVersion?.kind === 'PUBLISHED'

  const presentationStatus =
    data?.share?.version?.presentationStatus ?? 'PENDING'
  const isProcessingPresentationFile = !performPresentationFileCheck
    ? false
    : presentationStatus === 'PENDING'

  let canvasContent = <></>

  if (isProcessingPresentationFile) {
    canvasContent = (
      <LoadingState
        type="color-icon"
        size="48px"
        message={<LoadingMessage />}
      />
    )
  } else {
    canvasContent = (
      <ShareReadyGuard renderStatus={parsedRenderStatus}>
        {permanentPageId ? (
          <StyledCanvasViewWrapper>
            {showPagesList ? (
              <CanvasLeftPanel
                isInspectPanelOpen={isInspectPanelOpen}
                stateKey={`${share.identifier}:${permanentPageId}`}
                navigationComponent={
                  <PageSelector
                    shareIdentifier={share.identifier}
                    versionShortId={versionShortId}
                    permanentPageIdentifier={permanentPageId!}
                  />
                }
              />
            ) : null}
            <SharePageCanvas
              ref={canvasRef}
              isViewingLatestVersion={isViewingLatestVersion}
              permanentPageIdentifier={permanentPageId!}
            />

            {isTabletAndBigger && <DraftAnnotationNotifier />}
          </StyledCanvasViewWrapper>
        ) : (
          <ErrorWrapper>
            <ErrorMessage
              title="No pages in document"
              icon={<DocumentIcon />}
              iconSize="medium"
            >
              No pages have been added here yet.
            </ErrorMessage>
          </ErrorWrapper>
        )}
      </ShareReadyGuard>
    )
  }

  return (
    <CanvasRendererProvider>
      <CanvasInteractionsProvider>
        <SelectedFrameProvider>
          <CanvasLeftPanelResizingProvider>
            <FrameInspectorProvider
              autoSelectRootInitially={false}
              //  Reset context for each page
              key={permanentPageId}
            >
              <GridsAndLayoutsProvider>
                <AnnotationQueryVariablesProvider
                  shareIdentifier={share.identifier}
                  versionIdentifier={versionIdentifier}
                  subjects={subjects}
                >
                  <PageAnnotationsOverlayContext
                    disabled={disableAnnotations}
                    hidden={isInspectPanelOpen}
                    isViewingLatestVersion={isViewingLatestVersion}
                    permanentPageId={permanentPageId}
                  >
                    <HeaderPortal>
                      <PageCanvasViewHeader
                        share={share}
                        setSidebarLeftOpen={setSidebarLeftOpen}
                        toggleSidebarRight={toggleSidebarRight}
                        isSidebarRightOpen={isSidebarRightOpen}
                        isViewingLatestVersion={isViewingLatestVersion}
                        isViewingStarredVersion={isCurrentVersionStarred}
                        hasPendingPatches={hasPendingPatches}
                        userCanOpenInApp={
                          !!currentVersion?.document?.userCanOpenInApp
                        }
                        currentVersion={currentVersion || undefined}
                        hideSearch={isWorkspaceEncrypted}
                      />

                      <Panel
                        share={share}
                        version={currentVersion || undefined}
                        userCanSeeComments
                        userCanInspect={share.userCanInspect}
                        SidebarRightPortal={SidebarRightPortal}
                      />
                    </HeaderPortal>

                    <FloatingZoom />

                    <PageCanvasSupportGate>
                      {canvasContent}
                    </PageCanvasSupportGate>
                    {!IS_EMBEDDED && !isTabletAndBigger && (
                      <PageCanvasMobileToolBox
                        hideSearch={isWorkspaceEncrypted}
                      />
                    )}
                  </PageAnnotationsOverlayContext>
                </AnnotationQueryVariablesProvider>
              </GridsAndLayoutsProvider>
            </FrameInspectorProvider>
          </CanvasLeftPanelResizingProvider>
        </SelectedFrameProvider>
      </CanvasInteractionsProvider>
    </CanvasRendererProvider>
  )
}
